import { useEffect, useRef, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import {
  type AIMessage,
  type Citation,
  aiApi,
  streamMessage,
} from "../../lib/ai";
import "./chat.css";

// Detect course/lesson context from current URL so the assistant gets it for free.
function useRouteContext(): { courseId?: string; lessonId?: string } {
  const location = useLocation();
  const params = useParams();
  // /student/curs/:id or /admin/curs-editor/:id
  if (location.pathname.includes("/curs/") || location.pathname.includes("/curs-editor/")) {
    return { courseId: params.id };
  }
  return {};
}

type StreamState = "idle" | "loading" | "streaming" | "error";

export function ChatPanel({
  conversationId,
  onConversationChange,
  variant = "panel",
}: {
  conversationId: string | null;
  onConversationChange: (id: string) => void;
  variant?: "panel" | "page";
}) {
  const ctx = useRouteContext();
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [input, setInput] = useState("");
  const [state, setState] = useState<StreamState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [pendingText, setPendingText] = useState("");
  const [pendingCitations, setPendingCitations] = useState<Citation[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load conversation when ID changes
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    aiApi.get(conversationId).then((c) => setMessages(c.messages)).catch(() => {});
  }, [conversationId]);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, pendingText]);

  async function send() {
    const content = input.trim();
    if (!content || state === "loading" || state === "streaming") return;

    setInput("");
    setError(null);
    setState("loading");

    // Ensure we have a conversation
    let convId = conversationId;
    if (!convId) {
      try {
        const c = await aiApi.createConversation({ title: content.slice(0, 80) });
        convId = c.id;
        onConversationChange(c.id);
      } catch (e) {
        setError((e as Error).message);
        setState("error");
        return;
      }
    }

    // Optimistic: show the user message immediately
    const tempUserMsg: AIMessage = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content,
      created_at: new Date().toISOString(),
      citations: [],
    };
    setMessages((m) => [...m, tempUserMsg]);
    setPendingText("");
    setPendingCitations([]);
    setState("streaming");

    abortRef.current = new AbortController();
    let accumulated = "";
    let cites: Citation[] = [];

    await streamMessage(
      convId!,
      { content, lesson_id: ctx.lessonId, course_id: ctx.courseId },
      {
        onCitations: (c) => {
          cites = c;
          setPendingCitations(c);
        },
        onToken: (t) => {
          accumulated += t;
          setPendingText(accumulated);
        },
        onDone: ({ message_id }) => {
          const assistantMsg: AIMessage = {
            id: message_id,
            role: "assistant",
            content: accumulated,
            created_at: new Date().toISOString(),
            citations: cites,
          };
          setMessages((m) => [...m, assistantMsg]);
          setPendingText("");
          setPendingCitations([]);
          setState("idle");
        },
        onError: (detail) => {
          setError(detail);
          setState("error");
          setPendingText("");
          setPendingCitations([]);
        },
      },
      abortRef.current.signal,
    );
  }

  function abort() {
    abortRef.current?.abort();
    setState("idle");
    setPendingText("");
  }

  return (
    <div className={`chat chat--${variant}`}>
      <div className="chat-scroll" ref={scrollRef}>
        {messages.length === 0 && state === "idle" && (
          <div className="chat-empty">
            <h3>Asistent virtual</h3>
            <p>
              Întreabă-mă despre lecții, proceduri sau navigarea în platformă.
              Răspund pe baza cursurilor și citez sursele.
            </p>
            <p className="chat-disclaimer">
              Nu ofer diagnostice sau recomandări clinice personalizate — pentru
              decizii clinice individuale, consultă un specialist.
            </p>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {state === "streaming" && pendingText && (
          <MessageBubble
            message={{
              id: "streaming",
              role: "assistant",
              content: pendingText,
              created_at: new Date().toISOString(),
              citations: pendingCitations,
            }}
          />
        )}
        {(state === "loading" || (state === "streaming" && !pendingText)) && (
          <div className="msg msg--assistant">
            <div className="msg-content msg-content--thinking">
              <TypingIndicator />
            </div>
          </div>
        )}
        {state === "error" && error && <div className="chat-error">⚠ {error}</div>}
      </div>

      <div className="chat-input">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Întreabă ceva… (Enter = trimite, Shift+Enter = linie nouă)"
          rows={2}
          disabled={state === "loading" || state === "streaming"}
        />
        {state === "streaming" ? (
          <button className="btn btn-secondary" onClick={abort}>
            Oprește
          </button>
        ) : (
          <button
            className="btn btn-primary"
            onClick={send}
            disabled={!input.trim() || state === "loading"}
          >
            {state === "loading" ? "…" : "Trimite"}
          </button>
        )}
      </div>
    </div>
  );
}

// Animated three-dot indicator shown while waiting for the first token.
function TypingIndicator() {
  return (
    <span className="chat-typing" role="status" aria-label="Asistentul se gândește…">
      <span className="chat-typing-dot" />
      <span className="chat-typing-dot" />
      <span className="chat-typing-dot" />
    </span>
  );
}

function MessageBubble({ message }: { message: AIMessage }) {
  return (
    <div className={`msg msg--${message.role}`}>
      <div className="msg-content">{renderContentWithCitations(message.content)}</div>
      {message.citations.length > 0 && (
        <div className="msg-citations">
          {message.citations.map((c, i) => (
            <CitationChip key={`${c.lesson_id}-${i}`} index={i + 1} citation={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CitationChip({ index, citation }: { index: number; citation: Citation }) {
  const title = citation.lesson_title || "Sursă";
  const courseHint = citation.course_title ? ` · ${citation.course_title}` : "";
  return (
    <Link
      to={`/student/curs/${citation.course_id}`}
      className="citation-chip"
      title={citation.excerpt}
    >
      <span className="citation-num">[{index}]</span>
      <span className="citation-label">
        {title}
        {courseHint}
      </span>
    </Link>
  );
}

// Render [1], [2] references as small superscript pills inline with the text.
function renderContentWithCitations(content: string) {
  const parts = content.split(/(\[\d+\])/g);
  return parts.map((part, i) => {
    const match = part.match(/^\[(\d+)\]$/);
    if (match) {
      return (
        <sup key={i} className="citation-ref">
          {match[1]}
        </sup>
      );
    }
    return <span key={i}>{part}</span>;
  });
}
